import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { downloadBlob, ToastErrorService } from '@app/shared/services';
import { usersImporterActions } from '../actions/users-importer.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class UsersImporterEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    importLearners$ = createEffect(() => {
        return this.actions.pipe(
            ofType(usersImporterActions.importLearners),
            mergeMap(({ schoolId, file }) => {
                return this.httpService.importLearners(schoolId, file).pipe(
                    finalizeLoader('users-importer-import'),
                    map((payload) => usersImporterActions.importLearnersSuccess({ payload })),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.users-importer.importError');
                        return of(usersImporterActions.importLearnersError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    importLearnersSuccess$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(usersImporterActions.importLearnersSuccess),
                tap(({ payload }) => {
                    const { created, skipped, failed } = payload;
                    const message = this.translateService.instant('toast.users-importer.imported', {
                        created,
                        skipped,
                        failed,
                    });
                    this.messageService.add(message, failed > 0 ? 'warning' : 'success');
                }),
            );
        },
        { dispatch: false },
    );

    downloadTemplate$ = createEffect(() => {
        return this.actions.pipe(
            ofType(usersImporterActions.downloadTemplate),
            mergeMap(() => {
                return this.httpService.downloadLearnersTemplate().pipe(
                    finalizeLoader('users-importer-template'),
                    tap((blob) => downloadBlob(blob, 'learners-import-template.csv')),
                    map((blob) => usersImporterActions.downloadTemplateSuccess({ blob })),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.users-importer.templateError');
                        return of(usersImporterActions.downloadTemplateError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
